import { EscalationStatus } from "shared";
import {
  ArrowUturnLeftIcon,
  BellSnoozeIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import { When } from "react-if";

import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import {
  useMarkCancelledEscalationMutation,
  useMarkCompleteEscalationMutation,
  useMarkSnoozeEscalationMutation,
  useReopenEscalationMutation,
} from "../../../services/api/escalationApi/escalation";
import { classNames } from "../../../../common/lib/classNames";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import { Dropdown } from "../../../../common/Atoms/Dropdown";
import { useCurrentEscalation } from "../../../hooks/useCurrentEscalation";
import { Modal } from "../../../../common/Atoms/Modal";
import { Button } from "../../../../common/Atoms/Button";
import { DatePicker } from "../../../../common/Atoms/DatePicker";

export function EscalationActions() {
  const organisation = useGetSelectedOrganisation();
  const [completeApiCall, { isLoading: completeLoading }] = useMarkCompleteEscalationMutation();
  const [cancelApiCall, { isLoading: cancelLoading }] = useMarkCancelledEscalationMutation();
  const [snoozeApiCall, { isLoading: snoozeLoading }] = useMarkSnoozeEscalationMutation();
  const [reopenApiCall, { isLoading: reopenLoading }] = useReopenEscalationMutation();
  const { currentEscalation: escalation } = useCurrentEscalation();

  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customSnoozeDate, setCustomSnoozeDate] = useState(``);

  const snoozeOption = useMemo(() => {
    const options = [
      {
        label: `Tomorrow`,
        value: `1`,
      },
      {
        label: `3 Days Later`,
        value: `3`,
      },
      {
        label: `Next Week`,
        value: `7`,
      },
      {
        label: `Next Month`,
        value: `30`,
      },
      {
        label: `Custom...`,
        value: `custom`,
      },
    ];
    if (!escalation) return options;

    // we have an expected payment date and it's in the future
    if (escalation.invoice?.expected_payment_date_str && organisation.validatedTimezone) {

      const expPayDateTime = DateTime.fromISO(escalation.invoice.expected_payment_date_str, { zone: organisation.validatedTimezone });
      const orgTz = organisation.validatedTimezone;
      const now = DateTime.local().setZone(orgTz);

      if (expPayDateTime > now) {

        // round up to nearest full day for snoozing
        const days = Math.ceil(expPayDateTime.diff(now).as(`days`));

        options.push({
          label: `Until expected payment date`,
          // number of days from now to expected payment date
          value: `${days}`,
        });
      }
    }

    return options;
  }, [escalation]);

  function completeEscalation() {
    if (!escalation) return;

    completeApiCall({
      organisationId: escalation.organisationId,
      escalationId: escalation.id,
    });
  }

  function snoozeEscalation(e) {
    if (!escalation) return;

    // If custom option is selected, show the date picker modal
    if (e.value === `custom`) {
      setShowCustomDatePicker(true);

      return;
    }

    snoozeApiCall({
      organisationId: escalation.organisationId,
      escalationId: escalation.id,
      snoozeDays: e.value,
    });
  }

  async function handleCustomSnooze() {
    if (!escalation || !customSnoozeDate || !organisation.validatedTimezone) return;

    // Calculate days between now and selected date
    const selectedDate = DateTime.fromISO(customSnoozeDate, { zone: organisation.validatedTimezone });
    const now = DateTime.local().setZone(organisation.validatedTimezone);
    const days = Math.ceil(selectedDate.diff(now, `days`).days);

    if (days < 1) {
      alert(`Please select a future date`);

      return;
    }

    await snoozeApiCall({
      organisationId: escalation.organisationId,
      escalationId: escalation.id,
      snoozeDays: days.toString(),
    });

    setShowCustomDatePicker(false);
    setCustomSnoozeDate(``);
  }

  function handleCancelCustomSnooze() {
    setShowCustomDatePicker(false);
    setCustomSnoozeDate(``);
  }

  function cancelEscalation() {
    if (!escalation) return;

    cancelApiCall({
      organisationId: escalation.organisationId,
      escalationId: escalation.id,
    });
  }

  function reopenEscalation() {
    if (!escalation) return;

    reopenApiCall({
      organisationId: escalation.organisationId,
      escalationId: escalation.id,
    });
  }

  return (
    <div className={ `grid grid-cols-3 gap-2` }>
      { /* Close Escalation */ }
      <button
        className={ classNames(
          `flex flex-col items-center justify-center px-2 rounded-md hover:bg-gray-100`,
          (completeLoading || escalation?.escalationStatus !== EscalationStatus.PENDING) ? `opacity-50 cursor-not-allowed` : ``,
        ) }
        onClick={ () => completeEscalation() }
        disabled={ (completeLoading || escalation?.escalationStatus !== EscalationStatus.PENDING) }
      >
        <CheckIcon className={ `w-6 h-6 text-gray-500` } />
        <Paragraph
          as={ `span` }
          size={ `xs` }
        >
          { `Close Escalation` }
        </Paragraph>
      </button>

      { /* Snooze */ }
      <div
        className={ classNames(
          `flex flex-col items-center justify-center px-2`,
          (snoozeLoading || escalation?.escalationStatus !== EscalationStatus.PENDING) ? `opacity-50 cursor-not-allowed` : ``,
        ) }
        // onClick={ () => completeEscalation() }
      >
        <BellSnoozeIcon className={ `w-6 h-6 text-gray-500` } />
        <Dropdown
          size={ `sm` }
          position={ `right` }
          label={ `Snooze until...` }
          options={ [snoozeOption] }
          onSelect={ snoozeEscalation }
          disabled={ (snoozeLoading || escalation?.escalationStatus !== EscalationStatus.PENDING) }
          theme={ `clear` }
        />
      </div>

      <When condition={ escalation?.escalationStatus === EscalationStatus.PENDING }>
        { /* Cancel */ }
        <button
          className={ classNames(
            `flex flex-col items-center justify-center px-2 rounded-md hover:bg-gray-100`,
            (cancelLoading || escalation?.escalationStatus !== EscalationStatus.PENDING) ? `opacity-50 cursor-not-allowed` : ``,
          ) }
          onClick={ () => cancelEscalation() }
          disabled={ (cancelLoading || escalation?.escalationStatus !== EscalationStatus.PENDING) }
        >
          <XMarkIcon className={ `w-6 h-6 text-gray-500` } />
          <Paragraph
            as={ `span` }
            size={ `xs` }
          >
            { `Cancel Escalation` }
          </Paragraph>
        </button>
      </When>

      { /* Otherwise, show the reopen button */ }
      <When condition={ escalation?.escalationStatus !== EscalationStatus.PENDING }>
        <button
          className={ classNames(
            `flex flex-col items-center justify-center px-2 rounded-md hover:bg-gray-100`,
            (reopenLoading || escalation?.escalationStatus === EscalationStatus.PENDING) ? `opacity-50 cursor-not-allowed` : ``,
          ) }
          onClick={ () => reopenEscalation() }
          disabled={ (reopenLoading || escalation?.escalationStatus === EscalationStatus.PENDING) }
        >
          <ArrowUturnLeftIcon className={ `w-6 h-6 text-gray-500` } />
          <Paragraph
            as={ `span` }
            size={ `xs` }
          >
            { `Reopen Escalation` }
          </Paragraph>
        </button>
      </When>

      { /* Custom Date Picker Modal */ }
      <Modal
        open={ showCustomDatePicker }
        setOpen={ setShowCustomDatePicker }
        title={ `Select Custom Snooze Date` }
      >
        <div className={ `mt-4` }>
          <Paragraph className={ `mb-2` }>
            { `Snooze until:` }
          </Paragraph>
          <DatePicker
            value={ customSnoozeDate }
            onChange={ ({ value }) => setCustomSnoozeDate(value || ``) }
            placeholder={ `Select a date` }
          />
        </div>
        <div className={ `mt-6 flex justify-end gap-3` }>
          <Button
            color={ `gray` }
            onClick={ handleCancelCustomSnooze }
          >
            { `Cancel` }
          </Button>
          <Button
            onClick={ handleCustomSnooze }
            disabled={ !customSnoozeDate || snoozeLoading }
            loading={ snoozeLoading }
          >
            { `Snooze` }
          </Button>
        </div>
      </Modal>
    </div>
  );
}
